import { RequestError } from "api/hooks";
import { Error as ErrorComponent } from "components/Error";
import { Loader } from "components/Loader";
import { NUMBER_FORMAT_LOCALE } from "config";
import { memo, useCallback, useEffect, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { evalExprStr } from "./evaluator";
import { Promised } from "./types";

export const appendParamsToPathname = (pathname: string, params: URLSearchParams) =>
  pathname + (params.toString() === "" ? "" : "?" + params.toString());
export const appendParams = (window: Window | null, params: URLSearchParams) =>
  appendParamsToPathname(window?.location.pathname ?? "", params);
export const replaceWindowUrl = (window: Window | null, url: string) =>
  window?.history.replaceState("", "", url);
export const memoComponent = memo as <T>(component: T) => T;
export const getCartItemFromCart = (cart: any[], cartItemId: string) =>
  cart.find(({ id }) => cartItemId.toString() === id.toString());
export const assertIsOneOf = <T extends readonly string[]>(
  variants: T,
  value: string
): value is T[number] => variants.includes(value);

export const getId = (
  (id = 0) =>
  () =>
    `${id++}`
)();

export const emitEvent = (
  event: string,
  data?: unknown,
  target: EventTarget = window
) => {
  const _event = new CustomEvent(event, { detail: data });
  // console.log("emitting event", _event);

  target.dispatchEvent(_event);

  // console.log("emitted event", _event);
};

export const capitalize = (s: string) => `${s[0].toUpperCase()}${s.slice(1)}`;
export const promisedComponent = <T, E = unknown>(
  promise: Promised<T, E>,
  {
    resolved: Resolved,
    loading: Loading = Loader({ mode: "msgLong" }),
    rejected: Rejected = ({ err }: { err: E }) => {
      if (err instanceof RequestError)
        return <ErrorComponent err={err.payload.join("; ")} />;
      return <ErrorComponent err={(err as any).message} />;
    },
  }: {
    resolved: (props: { res: T }) => JSX.Element;
    loading?: (props: { title?: string }) => JSX.Element;
    rejected?: (props: { err: E }) => JSX.Element;
  }
) =>
  promise.state === "pending" ? (
    <Loading
      {...(typeof promise.data === "string" ? { title: promise.data } : promise.data)}
    />
  ) : promise.state === "resolved" ? (
    <Resolved res={promise.res} />
  ) : (
    <Rejected err={promise.err} />
  );

export const useMobileQuery = () => useMediaQuery({ maxWidth: 767 });

export const pick = <T extends {}, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => keys.includes(key as K))
  ) as Pick<T, K>;
};
export const omit = <T extends {}, K extends string>(obj: T, keys: K[]): Omit<T, K> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key as K))
  ) as Omit<T, K>;
};

export const randomInteger = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min) + min);

export const pickRandom = <T extends unknown[]>(values: T): T[number] =>
  values[randomInteger(0, values.length)];

export const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function assert(condition: any, msg?: string): asserts condition {
  if (!condition) {
    throw new Error(msg ?? "assertion failed");
  }
}

export const waitCond = async (cond: () => Promise<boolean>, timeout: number) =>
  await new Promise<void>((resolve) => {
    const f = () =>
      setTimeout(async () => {
        if (await cond()) resolve();
        else f();
      }, timeout); // check status every 15s

    f();
  });

export const pollEvent = async <T,>(f: () => Promise<{ done: boolean; event: T }>) => {
  while (true) {
    const { done, event } = await f();
    if (done) return event;
  }
};

export const reducePath =
  (target: Record<string, unknown>) =>
  (path: (string | number)[]): unknown =>
    path.reduce((obj, key) => obj?.[key] as Record<string, unknown>, target);

export const mapObj = <
  T,
  K extends string | number | symbol,
  T2,
  K2 extends string | number | symbol
>(
  obj: Record<K, T>,
  mapper: (val: [K, T]) => [K2, T2]
): Record<K2, T2> =>
  Object.fromEntries((Object.entries(obj) as unknown as [K, T][]).map(mapper)) as Record<
    K2,
    T2
  >;
export const mapObjKey = <
  T extends string | number | symbol,
  U extends string | number | symbol,
  V
>(
  obj: Record<T, V>,
  mapper: (val: [T, V]) => U
): Record<U, V> =>
  Object.fromEntries(
    Object.entries(obj).map(([key, val]) => [mapper([key, val] as [T, V]), val])
  ) as Record<U, V>;
export const mapObjValue = <T, U>(
  obj: Record<string, T>,
  mapper: (val: [string, T]) => U
): Record<string, U> =>
  Object.fromEntries(Object.entries(obj).map(([key, val]) => [key, mapper([key, val])]));

export const replacers = {
  dictionary:
    (dict: Record<string, unknown>, pathReducer = reducePath(dict)) =>
    (key: (string | number)[]) => {
      const property = pathReducer(key);
      return typeof property === "string" ? property : JSON.stringify(property);
    },
};

export const replace = (
  data: unknown,
  matcher: RegExp,
  replacer: (match: string, ...captures: (string | undefined)[]) => string
): typeof data => {
  if (typeof data === "string") {
    return stringReplace(data, matcher, replacer);
  } else if (Array.isArray(data)) {
    return data.map((it) => replace(it, matcher, replacer));
  } else if (typeof data === "object" && data !== null) {
    return mapObjValue(data as Record<string, unknown>, ([, value]) =>
      replace(value, matcher, replacer)
    );
  } else {
    return data;
  }
};

type Captures = (string | undefined)[];
type MatchData = [start: number, end: number, ...captures: Captures];
type Matcher = (str: string) => MatchData | null;
export const stringReplaceExt = (
  data: string,
  matcher: Matcher,
  replacer: (match: string, ...captures: Captures) => string
): typeof data => {
  while (true) {
    const matchData = matcher(data);
    if (!matchData) return data;

    const [matchStart, matchEnd, ...captures] = matchData;
    const match = data.slice(matchStart, matchEnd);
    const unmatchedLeft = data.slice(0, matchStart);
    const unmatchedRight = data.slice(matchEnd);
    const next = unmatchedLeft + replacer(match, ...captures) + unmatchedRight;

    if (next === data) return data;
    else data = next;
  }
};

export const stringReplace = (
  data: string,
  matcher: RegExp,
  replacer: (match: string, ...captures: Captures) => string
): typeof data => {
  while (data !== (data = data.replace(matcher, replacer))) {
    continue;
  }
  return data;
};

const contextReplaceMatcher: Matcher = (str: string) => {
  let start = 0;

  while (str.slice(start, start + 2) !== "\\(") {
    if (start > str.length) return null;
    ++start;
  }

  let end = start + 2;
  let capture = "";
  let balancing = 1;

  while (true) {
    if (end > str.length) return null;

    const char = str.charAt(end);

    if (char === ")") --balancing;
    if (char === "(") ++balancing;
    ++end;

    if (balancing === 0) break;
    capture += char;
  }

  return [start, end, capture];
};
export const contextReplace = (
  data: string,
  context: Record<string, unknown>
): typeof data => {
  return stringReplaceExt(data, contextReplaceMatcher, (match, expr = "") => {
    const value = evalExprStr(expr, context);
    let formatted = value?.toString();

    if (typeof value === "object") return JSON.stringify(value);
    if (typeof value === "number")
      formatted = Intl.NumberFormat(NUMBER_FORMAT_LOCALE, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

    return formatted;
  });
};
export const replaceLink = (data: string): { text: string; html: boolean } => {
  let html = false;
  return {
    text: stringReplace(
      data,
      /\[([^\]]*)\]\(([^)]*)\)/, // markdown-like syntax: [text](link)
      (match, text = "", link = "") => {
        html = true; // if found at least one such link - display text as html
        return `<a href='${link}' target="_blank" rel="noreferrer">${text}</a>`;
      }
    ),
    html,
  };
};
export const inspect =
  <T,>(f: (x: T) => void) =>
  (x: T) => {
    f(x);
    return x;
  };
export const getCookie = (name: string, doc = document) =>
  doc.cookie
    .split("; ")
    .find((item) => item.startsWith(name + "="))
    ?.split("=")
    .splice(1)
    .join("=");
export const setCookie = (
  name: string,
  value: string,
  options = {} as Record<string, unknown>
) => {
  options = {
    path: "/",
    // при необходимости добавьте другие значения по умолчанию
    ...options,
  };

  if (options.expires instanceof Date) {
    options.expires = options.expires.toUTCString();
  }

  let updatedCookie = encodeURIComponent(name) + "=" + encodeURIComponent(value);

  for (let optionKey in options) {
    updatedCookie += "; " + optionKey;
    let optionValue = options[optionKey];
    if (optionValue !== true) {
      updatedCookie += "=" + optionValue;
    }
  }

  document.cookie = updatedCookie;
};

// change if there is better way
export function clone<T>(objectToBeCloned: T): T {
  if (!(objectToBeCloned instanceof Object)) {
    return objectToBeCloned;
  }

  const objectClone = JSON.parse(JSON.stringify(objectToBeCloned));

  return objectClone;
}

const createScript = (url: string, id?: string) => {
  const script = document.createElement("script");

  script.src = url;
  script.async = true;
  if (id) script.setAttribute("id", id);

  document.body.appendChild(script);

  return () => {
    document.body.removeChild(script);
  };
};

export const useScript = (...args: Parameters<typeof createScript>) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => createScript(...args), args);

export const useTimeout = (delay: number, callback: () => void) => {
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reset = useCallback(() => {
    if (!timeout.current) return;
    clearTimeout(timeout.current);
    timeout.current = setTimeout(callback, delay);
  }, [delay, callback]);

  useEffect(() => {
    if (!timeout.current) timeout.current = setTimeout(callback, delay);
    else {
      clearTimeout(timeout.current);
      timeout.current = setTimeout(callback, delay);
    }
    return reset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return reset;
};

export const cssGrayscale = (grayscale = 0, alpha = 1) =>
  `rgba(${grayscale}, ${grayscale}, ${grayscale}, ${alpha})`;

export const numberFormatter =
  (locale?: string | string[], options?: Intl.NumberFormatOptions) => (value?: string) =>
    value !== undefined
      ? new Intl.NumberFormat(locale, options).format(Number(value))
      : "";

export const numberParser =
  (locale?: string | string[]) =>
  (val = "0.0") => {
    // for when the input gets cleared
    if (typeof val === "string" && !val.length) {
      val = "0.0";
    }

    // detecting and parsing between comma and dot
    const group = new Intl.NumberFormat(locale).format(1111).replace(/1/g, "");
    const decimal = new Intl.NumberFormat(locale).format(1.1).replace(/1/g, "");
    let reversedVal = val.replace(new RegExp("\\" + group, "g"), "");
    reversedVal = reversedVal.replace(new RegExp("\\" + decimal, "g"), ".");
    //  => 1232.21

    // removing everything except the digits and dot
    reversedVal = reversedVal.replace(/[^0-9.]/g, "");
    //  => 1232.21

    return Number.isNaN(reversedVal) ? "0" : reversedVal;
  };

export const getRandomId = (size = 8) => crypto.randomUUID().slice(0, size);
